
import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import "./MyOrders.css";
import { globalStore } from "stores/globalStore";
import Service from "services/service";
import closeButton from "../../assets/Images/close-button.png";
import closeWhite from "../../assets/Images/closeWhite.png";
import logoImg from "../../assets/Images/img6.png";
import { FaArrowRightLong } from "react-icons/fa6";
import { FiPackage, FiTruck, FiCheckCircle, FiBox, FiExternalLink, FiCopy, FiRotateCcw, FiCornerDownLeft, FiClipboard, FiThumbsUp } from "react-icons/fi";
import { Spin, Empty, message, Modal, Radio } from "antd";
import { formatDateDDMMYYYY } from "utils/dateFormat";

const MyOrders = () => {
  const { getOrdersData, setProductDetailsData, productDetailsData, darkMode } = globalStore();
  const [showProduct, setShowProduct] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [replaceModal, setReplaceModal] = useState({ open: false, order: null, product: null });
  const [selectedSize, setSelectedSize] = useState("");
  const [returnModal, setReturnModal] = useState({ open: false, order: null, product: null });
  const [returnReason, setReturnReason] = useState("");
  const [replaceReason, setReplaceReason] = useState("");

  // Fetch orders on component mount
  useEffect(() => {
    fetchOrders();
  }, []);

  const getDeliveryStatus = (order) => {
    return order.deliveryStatus || order.status || "";
  };

  const isReturnOrReplaceStatus = (status) => {
    const s = (status || "").toLowerCase();
    return s.startsWith("return") || s.startsWith("replacement");
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await Service.getAllOrders();
      console.log("=== ALL ORDERS RAW ===", JSON.stringify(response?.data?.data, null, 2));
      if (response?.data?.data) {
        setOrders(response.data.data);
      } else if (getOrdersData?.OrderDetails) {
        setOrders(getOrdersData.OrderDetails);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      message.error("Failed to fetch orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async (order) => {
    if (!order) return;

    try {
      // Fetch full product details for this order
      let productsList = order.products || order.items || [];
      try {
        const detailRes = await Service.getAllProductDetails(order.orderId || order._id);
        if (detailRes?.data?.data) {
          const fetched = detailRes.data.data.products || detailRes.data.data.items || [];
          if (fetched.length > 0) productsList = fetched;
        }
      } catch (e) {
        console.warn("Could not fetch order details for invoice, using order data", e);
      }

      console.log("=== INVOICE PRODUCTS ===", JSON.stringify(productsList, null, 2));

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;
      const brandColor = [112, 14, 232];
      const darkText = [33, 33, 33];
      const mutedText = [120, 120, 120];
      const orderId = order.orderId || order.quickTransactionId || order._id;
      const orderDate = formatDateDDMMYYYY(order.orderDate || order.estimatedDeliveryDate || Date.now());
      const totalAmount = order.totalAmount || order.finalAmount || 0;

      // --- Header band ---
      doc.setFillColor(...brandColor);
      doc.rect(0, 0, pageWidth, 38, "F");

      // Add logo
      try {
        const img = new Image();
        img.src = logoImg;
        doc.addImage(img, "PNG", margin, 3, 32, 32);
      } catch (e) {
        console.error("Logo load error:", e);
      }

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.text("Rekha Collection", margin + 34, 16);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("Affordable for Everyone", margin + 34, 23);
      doc.setFontSize(9);
      doc.text("rekhacollection.in", margin + 34, 29);
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("INVOICE", pageWidth - margin, 20, { align: "right" });
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated: ${formatDateDDMMYYYY(new Date())}`, pageWidth - margin, 30, { align: "right" });

      // --- Order info section ---
      const isDelivered = getDeliveryStatus(order).toLowerCase() === "delivered" || isReturnOrReplaceStatus(getDeliveryStatus(order));
      const deliveryDate = isDelivered
        ? formatDateDDMMYYYY(order.deliveredDate || order.estimatedDeliveryDate || Date.now())
        : null;

      let y = 52;
      const infoBoxHeight = 28;
      doc.setFillColor(245, 241, 255);
      doc.roundedRect(margin, y - 6, contentWidth, infoBoxHeight, 3, 3, "F");
      doc.setFontSize(9);
      doc.setTextColor(...mutedText);
      doc.text("ORDER ID", margin + 8, y + 2);
      doc.text("ORDER DATE", margin + 75, y + 2);
      if (isDelivered && deliveryDate) {
        doc.text("DELIVERED ON", margin + 140, y + 2);
      }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...darkText);
      doc.text(`${orderId}`, margin + 8, y + 14);
      doc.setFont("helvetica", "normal");
      doc.text(`${orderDate}`, margin + 75, y + 14);
      if (isDelivered && deliveryDate) {
        doc.setTextColor(34, 139, 34);
        doc.setFont("helvetica", "bold");
        doc.text(deliveryDate, margin + 140, y + 14);
      }

      // --- Shipping Details Section ---
      y = 52 + infoBoxHeight + 8;
      const shippingAddr = order.shippingAddress || order.address || {};
      const courierName = order.courierName || "";
      const trackingNum = order.courierTrackingNumber || order.trackingNumber || "";
      const customerName = order.customerName || order.fullName || shippingAddr.fullName || "";
      const customerPhone = shippingAddr.mobileNumber || order.mobileNumber || "";
      const addrLine1 = shippingAddr.addressLine1 || "";
      const addrLine2 = shippingAddr.addressLine2 || "";
      const addrCity = shippingAddr.city || "";
      const addrState = shippingAddr.state || "";
      const addrPin = shippingAddr.pinCode || "";
      const addrCountry = shippingAddr.country || "";
      const hasShipping = addrLine1 || courierName || trackingNum;

      if (hasShipping) {
        const halfWidth = (contentWidth - 10) / 2;

        // Section title
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...brandColor);
        doc.text("SHIPPING DETAILS", margin, y);
        y += 6;
        doc.setDrawColor(...brandColor);
        doc.setLineWidth(0.5);
        doc.line(margin, y, margin + contentWidth, y);
        y += 8;

        // Left column - Shipping Address
        const leftX = margin + 8;
        let leftY = y;
        doc.setFillColor(250, 248, 255);
        doc.roundedRect(margin, y - 5, halfWidth, 48, 3, 3, "F");

        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...mutedText);
        doc.text("SHIP TO", leftX, leftY + 2);
        leftY += 9;

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...darkText);
        if (customerName) {
          doc.text(customerName, leftX, leftY);
          leftY += 6;
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        if (addrLine1) { doc.text(addrLine1, leftX, leftY); leftY += 5; }
        if (addrLine2) { doc.text(addrLine2, leftX, leftY); leftY += 5; }
        const cityStatePin = [addrCity, addrState].filter(Boolean).join(", ") + (addrPin ? ` - ${addrPin}` : "");
        if (cityStatePin) { doc.text(cityStatePin, leftX, leftY); leftY += 5; }
        if (addrCountry) { doc.text(addrCountry, leftX, leftY); leftY += 5; }
        if (customerPhone) {
          doc.setTextColor(...mutedText);
          doc.text(`Phone: ${customerPhone}`, leftX, leftY);
        }

        // Right column - Courier Details
        const rightX = margin + halfWidth + 18;
        let rightY = y;
        doc.setFillColor(250, 248, 255);
        doc.roundedRect(margin + halfWidth + 10, y - 5, halfWidth, 48, 3, 3, "F");

        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...mutedText);
        doc.text("COURIER DETAILS", rightX, rightY + 2);
        rightY += 9;

        doc.setFontSize(9);
        doc.setTextColor(...darkText);
        if (courierName) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(...mutedText);
          doc.text("Courier Name", rightX, rightY);
          rightY += 5;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(...darkText);
          doc.text(courierName, rightX, rightY);
          rightY += 8;
        }
        if (trackingNum) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(...mutedText);
          doc.text("Tracking Number", rightX, rightY);
          rightY += 5;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(...brandColor);
          doc.text(trackingNum, rightX, rightY);
          rightY += 8;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...mutedText);
          doc.text("Track at: trackcourier.io", rightX, rightY);
        }
        if (!courierName && !trackingNum) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...mutedText);
          doc.text("Will be updated once shipped", rightX, rightY);
        }

        y += 52;
      }

      // --- Product table ---
      const colX = { num: margin, name: margin + 10, size: margin + 72, color: margin + 92, qty: margin + 120, price: margin + 142, total: margin + 165 };

      // Table header
      doc.setFillColor(...brandColor);
      doc.roundedRect(margin, y - 5, contentWidth, 12, 2, 2, "F");
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("#", colX.num + 3, y + 3);
      doc.text("PRODUCT", colX.name, y + 3);
      doc.text("SIZE", colX.size, y + 3);
      doc.text("COLOUR", colX.color, y + 3);
      doc.text("QTY", colX.qty, y + 3);
      doc.text("PRICE", colX.price, y + 3);
      doc.text("TOTAL", colX.total, y + 3);

      y += 14;
      let subtotal = 0;

      // Helper: recursively find first truthy numeric value by key patterns
      const findNumericField = (obj, patterns, depth = 0) => {
        if (!obj || typeof obj !== 'object' || depth > 3) return 0;
        for (const key of Object.keys(obj)) {
          const lowerKey = key.toLowerCase();
          const val = obj[key];
          if (typeof val === 'number' && val > 0) {
            for (const pattern of patterns) {
              if (lowerKey.includes(pattern)) return val;
            }
          }
          if (typeof val === 'string' && !isNaN(val) && Number(val) > 0) {
            for (const pattern of patterns) {
              if (lowerKey.includes(pattern)) return Number(val);
            }
          }
          if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
            const nested = findNumericField(val, patterns, depth + 1);
            if (nested > 0) return nested;
          }
        }
        return 0;
      };

      const findStringField = (obj, patterns, depth = 0) => {
        if (!obj || typeof obj !== 'object' || depth > 3) return "";
        for (const key of Object.keys(obj)) {
          const lowerKey = key.toLowerCase();
          const val = obj[key];
          if (typeof val === 'string' && val) {
            for (const pattern of patterns) {
              if (lowerKey.includes(pattern)) return val;
            }
          }
          if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
            const nested = findStringField(val, patterns, depth + 1);
            if (nested) return nested;
          }
        }
        return "";
      };

      console.log("=== FULL ORDER DATA ===", JSON.stringify(order, null, 2));

      productsList.forEach((product, index) => {
        if (y > pageHeight - 50) {
          doc.addPage();
          y = 20;
        }

        console.log(`Invoice Product [${index}]:`, JSON.stringify(product, null, 2));

        const qty = findNumericField(product, ['quant', 'qty', 'count']) || 1;
        const price = findNumericField(product, ['discount', 'selling', 'unit', 'sale', 'mrp', 'price', 'amount', 'cost', 'rate']);
        const lineTotal = price > 0 ? qty * price : findNumericField(product, ['total', 'linetotal', 'subtotal']);
        subtotal += lineTotal > 0 ? lineTotal : 0;
        const name = (findStringField(product, ['productname', 'name', 'title', 'item']) || "Item").substring(0, 28);
        const size = findStringField(product, ['tshirtsize', 'size']) || "N/A";
        const rawColour = findStringField(product, ['fontcolor', 'color', 'colour']) || "";

        // Convert hex/css color to closest name
        const colorNameMap = {
          '#000000': 'Black', '#ffffff': 'White', '#ff0000': 'Red', '#00ff00': 'Green',
          '#0000ff': 'Blue', '#ffff00': 'Yellow', '#ff00ff': 'Pink', '#00ffff': 'Cyan',
          '#808080': 'Grey', '#c0c0c0': 'Silver', '#800000': 'Maroon', '#808000': 'Olive',
          '#008000': 'Green', '#800080': 'Purple', '#008080': 'Teal', '#000080': 'Navy',
          '#ffa500': 'Orange', '#a52a2a': 'Brown', '#f5f5dc': 'Beige', '#ffc0cb': 'Pink',
          '#d2691e': 'Chocolate', '#ff6347': 'Tomato', '#4b0082': 'Indigo', '#ee82ee': 'Violet',
          '#ffd700': 'Gold', '#f0e68c': 'Khaki', '#e6e6fa': 'Lavender', '#fa8072': 'Salmon',
          '#40e0d0': 'Turquoise', '#ff7f50': 'Coral', '#dc143c': 'Crimson', '#2f4f4f': 'Dark Grey',
          'black': 'Black', 'white': 'White', 'red': 'Red', 'blue': 'Blue', 'green': 'Green',
          'yellow': 'Yellow', 'pink': 'Pink', 'orange': 'Orange', 'purple': 'Purple',
          'grey': 'Grey', 'gray': 'Grey', 'brown': 'Brown', 'navy': 'Navy', 'maroon': 'Maroon',
          'teal': 'Teal', 'cyan': 'Cyan', 'gold': 'Gold', 'silver': 'Silver', 'beige': 'Beige',
          'khaki': 'Khaki', 'coral': 'Coral', 'indigo': 'Indigo', 'violet': 'Violet',
        };

        const getColorName = (val) => {
          if (!val) return "N/A";
          const lower = val.toLowerCase().trim();
          if (colorNameMap[lower]) return colorNameMap[lower];
          // Check if it's already a readable name (no # prefix)
          if (!lower.startsWith('#') && !lower.startsWith('rgb')) {
            return val.charAt(0).toUpperCase() + val.slice(1);
          }
          // Find closest hex match
          if (lower.startsWith('#')) {
            const hex = lower.length === 4
              ? `#${lower[1]}${lower[1]}${lower[2]}${lower[2]}${lower[3]}${lower[3]}`
              : lower;
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            let closest = "N/A", minDist = Infinity;
            for (const [code, name] of Object.entries(colorNameMap)) {
              if (!code.startsWith('#')) continue;
              const cr = parseInt(code.slice(1, 3), 16);
              const cg = parseInt(code.slice(3, 5), 16);
              const cb = parseInt(code.slice(5, 7), 16);
              const dist = Math.sqrt((r - cr) ** 2 + (g - cg) ** 2 + (b - cb) ** 2);
              if (dist < minDist) { minDist = dist; closest = name; }
            }
            return closest;
          }
          return "N/A";
        };

        const colour = getColorName(rawColour);

        // Alternating row background
        if (index % 2 === 0) {
          doc.setFillColor(250, 248, 255);
          doc.rect(margin, y - 5, contentWidth, 12, "F");
        }

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...darkText);
        doc.text(`${index + 1}`, colX.num + 3, y + 3);
        doc.text(name, colX.name, y + 3);
        doc.text(size, colX.size, y + 3);
        // Draw color circle + name
        const hexToRgb = (hex) => {
          if (!hex || !hex.startsWith('#')) return null;
          const h = hex.length === 4
            ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
            : hex;
          return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
        };
        const cssColorHex = {
          black: '#000000', white: '#ffffff', red: '#ff0000', blue: '#0000ff', green: '#008000',
          yellow: '#ffff00', pink: '#ffc0cb', orange: '#ffa500', purple: '#800080', grey: '#808080',
          gray: '#808080', brown: '#a52a2a', navy: '#000080', maroon: '#800000', teal: '#008080',
          cyan: '#00ffff', gold: '#ffd700', silver: '#c0c0c0', beige: '#f5f5dc', khaki: '#f0e68c',
          coral: '#ff7f50', indigo: '#4b0082', violet: '#ee82ee', crimson: '#dc143c', chocolate: '#d2691e',
          salmon: '#fa8072', turquoise: '#40e0d0', lavender: '#e6e6fa', tomato: '#ff6347', olive: '#808000',
        };
        const colorHex = rawColour.startsWith('#') ? rawColour : (cssColorHex[rawColour.toLowerCase()] || null);
        const rgb = hexToRgb(colorHex);
        if (rgb) {
          doc.setFillColor(rgb[0], rgb[1], rgb[2]);
          doc.circle(colX.color + 3, y + 1.5, 3, "F");
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.3);
          doc.circle(colX.color + 3, y + 1.5, 3, "S");
        } else {
          doc.text(colour.substring(0, 10), colX.color, y + 3);
        }
        doc.text(`${qty}`, colX.qty + 5, y + 3);
        doc.text(`Rs. ${Number(price).toLocaleString("en-IN")}`, colX.price, y + 3);
        doc.setFont("helvetica", "bold");
        doc.text(`Rs. ${Number(lineTotal).toLocaleString("en-IN")}`, colX.total, y + 3);

        y += 12;
      });

      // Divider line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(margin, y, margin + contentWidth, y);
      y += 8;

      // --- Summary section ---
      // If subtotal is 0 (price fields missing on products), fall back to totalAmount
      const displaySubtotal = subtotal > 0 ? subtotal : Number(totalAmount);
      const summaryX = margin + 100;
      const valueX = pageWidth - margin;

      // Total Price (subtotal)
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...mutedText);
      doc.text("Total Price:", summaryX, y);
      doc.setTextColor(...darkText);
      doc.text(`Rs. ${Number(displaySubtotal).toLocaleString("en-IN")}`, valueX, y, { align: "right" });

      y += 10;
      const discount = displaySubtotal - Number(totalAmount);
      if (discount > 0) {
        doc.setTextColor(...mutedText);
        doc.text("Discount:", summaryX, y);
        doc.setTextColor(34, 139, 34);
        doc.text(`- Rs. ${Number(discount).toLocaleString("en-IN")}`, valueX, y, { align: "right" });
        y += 10;
      }

      // Total Amount box
      const totalBoxWidth = pageWidth - margin - summaryX + 10;
      doc.setFillColor(...brandColor);
      doc.roundedRect(summaryX - 5, y - 5, totalBoxWidth, 14, 2, 2, "F");
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text("Total Amount:", summaryX, y + 5);
      doc.text(`Rs. ${Number(totalAmount).toLocaleString("en-IN")}`, valueX, y + 5, { align: "right" });

      // --- Footer on every page ---
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const footerY = pageHeight - 15;
        doc.setDrawColor(...brandColor);
        doc.setLineWidth(0.5);
        doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(...mutedText);
        doc.text("Thank you for shopping with Rekha Collection!", pageWidth / 2, footerY, { align: "center" });
        doc.setFontSize(7);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, footerY + 5, { align: "right" });
      }

      doc.save(`Invoice_${orderId}.pdf`);
      message.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate invoice");
    }
  };

  const handleGetAllProduct = async (orderId) => {
    if (!orderId) {
      message.error("Invalid order ID");
      return;
    }
    
    try {
      setLoading(true);
      const response = await Service.getAllProductDetails(orderId);
      if (response?.data?.data) {
        setProductDetailsData({
          ProductData: response.data.data.products || response.data.data.items || [],
        });
        setShowProduct(true);
      } else {
        message.error("No products found for this order");
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      message.error("Failed to fetch product details");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToOrders = () => {
    setShowProduct(false);
    setProductDetailsData({ ProductData: [] });
  };

  const handleViewStatus = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const TRACKER_URL = "https://trackcourier.io/";
  const PROCESSING_FEE = 100;

  const isEligibleForReturnReplace = (order) => {
    const status = getDeliveryStatus(order).toLowerCase();
    if (status !== "delivered") return false;
    const deliveredDate = new Date(order.deliveredDate || order.estimatedDeliveryDate);
    const now = new Date();
    const diffDays = (now - deliveredDate) / (1000 * 60 * 60 * 24);
    return diffDays <= 3;
  };

  const getDaysLeft = (order) => {
    const deliveredDate = new Date(order.deliveredDate || order.estimatedDeliveryDate);
    const now = new Date();
    return Math.max(0, Math.ceil(3 - (now - deliveredDate) / (1000 * 60 * 60 * 24)));
  };

  const openReplaceModal = async (order, product) => {
    setSelectedSize(product?.size || product?.customization?.tShirtSize || "");
    setReplaceReason("");
    setReplaceModal({ open: true, order, product: { ...product, availableSizes: [], stockDetails: [] } });

    try {
      // Step 1: Call /v1/api/order/{orderId} to get productId
      const orderId = order.orderId || order._id;
      const orderRes = await Service.getAllProductDetails(orderId);
      const products = orderRes?.data?.data?.products || orderRes?.data?.data?.items || [];
      const matchedProduct = products[0];
      const productId = matchedProduct?.productId?._id || matchedProduct?.productId || matchedProduct?._id;

      if (productId) {
        // Step 2: Call fetchProductDetailsById to get available sizes & stock
        const productRes = await Service.getProductDetailsById(productId);
        const fullProduct = productRes?.data?.data || productRes?.data;
        const sizes = fullProduct?.size || fullProduct?.sizes || fullProduct?.availableSizes || [];
        const stockDetails = fullProduct?.stockDetails || fullProduct?.stock || [];

        setReplaceModal(prev => ({
          ...prev,
          product: {
            ...prev.product,
            availableSizes: sizes.length > 0 ? sizes : (prev.product.size ? [prev.product.size] : []),
            stockDetails: Array.isArray(stockDetails) ? stockDetails : [],
          },
        }));
        return;
      }
    } catch (e) {
      console.warn("Could not fetch product sizes", e);
    }

    // Fallback: include the current size
    const currentSize = product?.size || product?.customization?.tShirtSize;
    if (currentSize) {
      setReplaceModal(prev => ({
        ...prev,
        product: { ...prev.product, availableSizes: [currentSize] },
      }));
    }
  };

  const initiateRazorpayPayment = (razorpayOrderId, amount, description, onSuccess) => {
    const options = {
      key: "rzp_test_SZtOPUNSPfR6ze",
      amount: amount * 100,
      currency: "INR",
      name: "Rekha Collection",
      description,
      order_id: razorpayOrderId,
      handler: onSuccess,
      theme: { color: "#700ee8" },
    };
    const razorpay = new window.Razorpay(options);
    razorpay.on("payment.failed", () => message.error("Payment failed. Please try again."));
    razorpay.open();
  };

  const handleReplace = async () => {
    if (!replaceReason) { message.warning("Please select a reason"); return; }
    if (!selectedSize) { message.warning("Please select a size"); return; }

    const order = replaceModal.order;
    const orderId = order.orderId || order._id;

    try {
      setLoading(true);

      // 1. Call orderReplaceRequest → backend creates Razorpay order for ₹100
      const replaceRes = await Service.orderReplaceRequest({
        orderId,
        reason: replaceReason,
      });

      console.log("=== orderReplaceRequest (replace) response ===", JSON.stringify(replaceRes, null, 2));

      const razorpayOrderId = replaceRes.data?.razorpayOrderId
        || replaceRes.data?.orderId
        || replaceRes.data?.order_id
        || replaceRes.data?.id
        || replaceRes.razorpayOrderId
        || replaceRes.orderId
        || replaceRes.order_id;

      if (!razorpayOrderId) {
        message.error("Failed to create replacement request. Please try again.");
        return;
      }

      // 2. Open Razorpay → collect ₹100 processing fee
      initiateRazorpayPayment(
        razorpayOrderId,
        PROCESSING_FEE,
        `Replacement fee - Order #${orderId}`,
        async (response) => {
          try {
            // 3. On payment success → verify via orderReplaceVerify
            await Service.orderReplaceVerify({
              razorpayOrderId,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });

            message.success("Replacement requested successfully!");
            setOrders(prev => prev.map(o =>
              (o.orderId || o._id) === orderId
                ? { ...o, deliveryStatus: "Replacement Initiated" }
                : o
            ));
            setReplaceModal({ open: false, order: null, product: null });
          } catch (err) {
            console.error("Replace verify error:", err);
            message.error("Payment collected but verification failed. Contact support.");
          }
        }
      );
    } catch (error) {
      console.error("Error initiating replacement:", error);
      message.error(error.response?.data?.meta?.message || "Failed to initiate replacement");
    } finally {
      setLoading(false);
    }
  };

  const openReturnModal = (order, product) => {
    setReturnReason("");
    setReturnModal({ open: true, order, product });
  };

  const handleReturn = async () => {
    if (!returnReason) { message.warning("Please select a reason"); return; }

    const order = returnModal.order;
    const orderId = order.orderId || order._id;

    try {
      setLoading(true);

      // 1. Call orderReturnRequest → backend creates Razorpay order for ₹100
      const returnRes = await Service.orderReturnRequest({
        orderId,
        reason: returnReason,
      });

      console.log("=== orderReturnRequest response ===", JSON.stringify(returnRes, null, 2));

      const razorpayOrderId = returnRes.data?.razorpayOrderId
        || returnRes.data?.orderId
        || returnRes.data?.order_id
        || returnRes.data?.id
        || returnRes.razorpayOrderId
        || returnRes.orderId
        || returnRes.order_id;

      if (!razorpayOrderId) {
        message.error("Failed to create return request. Please try again.");
        return;
      }

      // 2. Open Razorpay → collect ₹100 processing fee
      initiateRazorpayPayment(
        razorpayOrderId,
        PROCESSING_FEE,
        `Return processing fee - Order #${orderId}`,
        async (response) => {
          try {
            // 3. On payment success → verify via orderReturnVerify
            await Service.orderReturnVerify({
              razorpayOrderId,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });

            message.success("Return requested! Full refund will be processed in 5-7 business days.");
            setOrders(prev => prev.map(o =>
              (o.orderId || o._id) === orderId
                ? { ...o, deliveryStatus: "Return Initiated" }
                : o
            ));
            setReturnModal({ open: false, order: null, product: null });
          } catch (err) {
            console.error("Return verify error:", err);
            message.error("Payment collected but verification failed. Contact support.");
          }
        }
      );
    } catch (error) {
      console.error("Error initiating return:", error);
      message.error(error.response?.data?.meta?.message || "Failed to initiate return");
    } finally {
      setLoading(false);
    }
  };

  const returnSteps = [
    { label: "Initiated", icon: <FiClipboard size={18} />, desc: "Return initiated" },
    { label: "Accepted", icon: <FiCheckCircle size={18} />, desc: "Return accepted" },
    { label: "Picked", icon: <FiCornerDownLeft size={18} />, desc: "Item picked up" },
    { label: "Completed", icon: <FiThumbsUp size={18} />, desc: "Return completed" },
  ];

  const replacementSteps = [
    { label: "Initiated", icon: <FiClipboard size={18} />, desc: "Replacement initiated" },
    { label: "Accepted", icon: <FiCheckCircle size={18} />, desc: "Replacement accepted" },
    { label: "Shipped", icon: <FiTruck size={18} />, desc: "Replacement shipped" },
    { label: "Completed", icon: <FiThumbsUp size={18} />, desc: "Replacement completed" },
  ];

  const getReturnReplaceIndex = (status) => {
    const map = {
      "return initiated": 0, "replacement initiated": 0,
      "return accepted": 1, "replacement accepted": 1,
      "return picked": 2, "replacement shipped": 2,
      "return completed": 3, "replacement completed": 3,
    };
    return map[(status || "").toLowerCase()] ?? 0;
  };

  const isReplacementStatus = (status) => {
    return (status || "").toLowerCase().startsWith("replacement");
  };

  const renderReturnReplaceStepper = (order) => {
    const status = getDeliveryStatus(order);
    if (!status || !isReturnOrReplaceStatus(status)) return null;
    const isReplacement = isReplacementStatus(status);
    const steps = isReplacement ? replacementSteps : returnSteps;
    const currentIdx = getReturnReplaceIndex(status);
    const type = isReplacement ? "Replacement" : "Return";

    return (
      <div className="rr-stepper-section">
        <div className="rr-stepper-header">
          <span className={`rr-badge ${isReplacement ? "replacement" : "return"}`}>{type}</span>
          <span className="rr-status-text">{status}</span>
        </div>
        <div className="tracking-stepper">
          {steps.map((step, index) => {
            const isCompleted = index <= currentIdx;
            const isCurrent = index === currentIdx;
            return (
              <div key={index} className="tracking-step">
                {index > 0 && (
                  <div className={`tracking-line ${isCompleted || isCurrent ? "line-active rr-line" : ""}`} />
                )}
                <div className={`tracking-icon ${isCompleted ? "icon-active rr-icon-active" : ""} ${isCurrent ? "icon-current" : ""}`}>
                  {step.icon}
                </div>
                <div className="tracking-step-info">
                  <span className={`tracking-step-label ${isCompleted ? "label-active" : ""}`}>{step.label}</span>
                  <span className="tracking-step-desc">{step.desc}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getStatusIndex = (status) => {
    const statusMap = {
      "ordered": 0, "processing": 0, "confirmed": 0,
      "packed": 1, "ready": 1,
      "shipped": 2, "out for delivery": 2,
      "delivered": 3,
      "return initiated": 3, "return accepted": 3, "return picked": 3, "return completed": 3,
      "replacement initiated": 3, "replacement accepted": 3, "replacement shipped": 3, "replacement completed": 3,
    };
    return statusMap[(status || "").toLowerCase()] ?? 0;
  };

  const copyTrackingNumber = (trackingNumber) => {
    navigator.clipboard.writeText(trackingNumber);
    message.success("Tracking number copied!");
  };

  const trackingSteps = [
    { label: "Ordered", icon: <FiBox size={20} />, desc: "Order confirmed" },
    { label: "Packed", icon: <FiPackage size={20} />, desc: "Ready to ship" },
    { label: "Shipped", icon: <FiTruck size={20} />, desc: "On the way" },
    { label: "Delivered", icon: <FiCheckCircle size={20} />, desc: "Received" },
  ];

  const renderTracking = (order) => {
    const currentIdx = getStatusIndex(getDeliveryStatus(order));
    const courierName = order.courierName || "";
    const trackingNumber = order.courierTrackingNumber || order.trackingNumber || "";

    return (
      <div className="tracking-section">
        {/* Courier Info */}
        {(courierName || trackingNumber) && (
          <div className="courier-info-bar">
            {courierName && (
              <div className="courier-field">
                <span className="courier-label">Courier Name</span>
                <span className="courier-value">{courierName}</span>
              </div>
            )}
            {trackingNumber && (
              <div className="courier-field">
                <span className="courier-label">Tracking Number</span>
                <span className="courier-value tracking-number-value">
                  {trackingNumber}
                  <FiCopy className="copy-icon" onClick={() => copyTrackingNumber(trackingNumber)} title="Copy" />
                </span>
              </div>
            )}
            {trackingNumber && (
              <a
                href={`${TRACKER_URL}?tracking_number=${trackingNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="track-courier-btn"
              >
                Track Courier <FiExternalLink size={14} />
              </a>
            )}
          </div>
        )}

        {/* Stepper */}
        <div className="tracking-stepper">
          {trackingSteps.map((step, index) => {
            const isCompleted = index <= currentIdx;
            const isCurrent = index === currentIdx;
            return (
              <div key={index} className="tracking-step">
                {index > 0 && (
                  <div className={`tracking-line ${index <= currentIdx ? "line-active" : ""}`} />
                )}
                <div className={`tracking-icon ${isCompleted ? "icon-active" : ""} ${isCurrent ? "icon-current" : ""}`}>
                  {step.icon}
                </div>
                <div className="tracking-step-info">
                  <span className={`tracking-step-label ${isCompleted ? "label-active" : ""}`}>{step.label}</span>
                  <span className="tracking-step-desc">{step.desc}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="orders-container">
        <div className="loading-container">
          <Spin size="large" />
          <p>Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-container">
      {showProduct ? (
        <div className="products-view">
          <button className="close-button" onClick={handleBackToOrders}>
            <img src={darkMode ? closeWhite : closeButton} alt="Close" />
          </button>
          <h2 className="orders-title">Your Products</h2>
          {productDetailsData?.ProductData?.length > 0 ? (
            <div className="product-list">
              {productDetailsData.ProductData.map((product, index) => (
                <div className="product-card" key={index}>
                  <img
                    src={product.primaryImageUrl || product.primaryImage}
                    alt={product.productName || product.name}
                    className="product-image"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                    }}
                  />
                  <div className="product-info">
                    <h3 className="product-name">{product.productName || product.name}</h3>
                    <p className="product-category">₹ {product.discountPrice || product.price}</p>
                    <p className="product-category">Size: {product.customization?.tShirtSize || product.size || "N/A"}</p>
                    <p className="product-category">Quantity: {product.quantity || 1}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty description="No products found" />
          )}
        </div>
      ) : (
        <>
          <h2 className="orders-title">Your Orders</h2>
          {orders && orders.length > 0 ? (
            orders.map((order) => (
              <div className="order-card" key={order.orderId || order._id}>
                <div className="order-header">
                  <div className="order-info">
                    <p className="order-date">
                      ORDER PLACED: {formatDateDDMMYYYY(order.orderDate || order.estimatedDeliveryDate || Date.now())}
                    </p>
                    <p className="order-total">TOTAL: ₹{order.totalAmount || order.finalAmount}</p>
                  </div>
                  <div className="order-id">
                    <p className="order-id_number">
                      ORDER#: {order.quickTransactionId || order.orderId || order._id}
                    </p>
                    <div className="order-links">
                      {order.products && order.products.length > 1 && (
                        <div className="all-product-details">
                          <a onClick={() => handleGetAllProduct(order.orderId || order._id)}>
                            View all products
                          </a>
                        </div>
                      )}
                      <div className="kfjdhjdjdhdh">
                        <p className="Invoice_gen" onClick={() => generatePDF(order)}>
                          View Invoice
                        </p>
                        <p>|</p>
                        <p className="view-status" onClick={() => handleViewStatus(order.orderId || order._id)}>
                          {expandedOrderId === (order.orderId || order._id) ? "Hide Status" : "View Status"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="all-order">
                  <div className="order-body">
                    <div className="product-image">
                      <img
                        src={order.products?.[0]?.primaryImageUrl || order.items?.[0]?.primaryImage}
                        alt={order.products?.[0]?.productName || order.items?.[0]?.name}
                        className="product-thumbnail"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/60x60?text=No+Image';
                        }}
                      />
                    </div>
                    <div className="prOrder-details">
                      <h3 className="product-name">{order.products?.[0]?.productName || order.items?.[0]?.name || "Product"}</h3>
                      <p className="product-count">
                        Products in this order: {order.products?.length || order.items?.length || 1}
                      </p>
                      <p className="delivery-date">
                        {isReturnOrReplaceStatus(getDeliveryStatus(order))
                          ? `Status: ${getDeliveryStatus(order)}`
                          : getDeliveryStatus(order).toLowerCase() === "delivered"
                            ? `Delivered on: ${formatDateDDMMYYYY(order.deliveredDate || order.estimatedDeliveryDate || Date.now())}`
                            : `Estimated Delivery: ${formatDateDDMMYYYY(order.estimatedDeliveryDate || Date.now())}`
                        }
                      </p>
                    </div>
                  </div>
                </div>
                
                {expandedOrderId === (order.orderId || order._id) && (
                  <div className="status-details">
                    {renderTracking(order)}
                    {renderReturnReplaceStepper(order)}
                  </div>
                )}

                {/* Return / Replace buttons */}
                {isEligibleForReturnReplace(order) && !isReturnOrReplaceStatus(getDeliveryStatus(order)) && (
                  <div className="rr-actions-bar">
                    <span className="rr-eligibility-text">
                      {getDaysLeft(order)} day{getDaysLeft(order) !== 1 ? "s" : ""} left for return/replace
                    </span>
                    <div className="rr-buttons">
                      <button className="rr-btn rr-btn-replace" onClick={() => openReplaceModal(order, order.products?.[0])}>
                        <FiRotateCcw size={14} /> Replace
                      </button>
                      <button className="rr-btn rr-btn-return" onClick={() => openReturnModal(order, order.products?.[0])}>
                        <FiCornerDownLeft size={14} /> Return
                      </button>
                    </div>
                  </div>
                )}
                {isReturnOrReplaceStatus(getDeliveryStatus(order)) && expandedOrderId !== (order.orderId || order._id) && (
                  <div className="rr-actions-bar">
                    <span className={`rr-badge ${isReplacementStatus(getDeliveryStatus(order)) ? "replacement" : "return"}`}>
                      {isReplacementStatus(getDeliveryStatus(order)) ? "Replacement" : "Return"}
                    </span>
                    <span className="rr-status-text">{getDeliveryStatus(order)}</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="empty-orders">
              <Empty 
                description="No orders found" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <p>You haven't placed any orders yet.</p>
                <button 
                  className="shop-now-btn"
                  onClick={() => window.location.href = "/"}
                >
                  Start Shopping
                </button>
              </Empty>
            </div>
          )}
        </>
      )}

      {/* Replace Modal */}
      <Modal
        title="Replace Product"
        open={replaceModal.open}
        onCancel={() => setReplaceModal({ open: false, order: null, product: null })}
        onOk={handleReplace}
        okText={`Pay ₹${PROCESSING_FEE} & Replace`}
        confirmLoading={loading}
        cancelText="Cancel"
      >
        {replaceModal.product && (
          <div className="rr-modal-content">
            <div className="rr-modal-product">
              <img src={replaceModal.product.primaryImageUrl} alt={replaceModal.product.productName} className="rr-modal-img" />
              <div>
                <h4>{replaceModal.product.productName}</h4>
                <p className="rr-modal-color">Color: <strong>{replaceModal.product.color || "N/A"}</strong> (cannot be changed)</p>
                <p>Current Size: <strong>{replaceModal.product.size || "N/A"}</strong></p>
              </div>
            </div>
            <div className="rr-modal-sizes">
              <p className="rr-modal-label">Select Size for Replacement:</p>
              <div className="rr-size-options">
                {(replaceModal.product.availableSizes && replaceModal.product.availableSizes.length > 0
                  ? replaceModal.product.availableSizes
                  : replaceModal.product.size ? [replaceModal.product.size] : []
                ).map(size => {
                  const stockInfo = replaceModal.product.stockDetails?.find(s => s.size === size);
                  const inStock = stockInfo ? stockInfo.stock > 0 : true;
                  return (
                    <label key={size} className={`rr-size-checkbox ${selectedSize === size ? "rr-size-checked" : ""} ${!inStock ? "rr-size-out-of-stock" : ""}`}>
                      <input
                        type="checkbox"
                        checked={selectedSize === size}
                        onChange={() => inStock && setSelectedSize(selectedSize === size ? "" : size)}
                        className="rr-size-input"
                        disabled={!inStock}
                      />
                      <span className="rr-size-label">{size}</span>
                      {stockInfo && <span className={`rr-stock-info ${inStock ? "in-stock" : "out-of-stock"}`}>{inStock ? `(${stockInfo.stock} left)` : "(Out of stock)"}</span>}
                    </label>
                  );
                })}
              </div>
              {!selectedSize && <p className="rr-size-hint">Please select a size to proceed</p>}
            </div>
            <div className="rr-modal-reason">
              <p className="rr-modal-label">Reason for Replacement:</p>
              <Radio.Group onChange={(e) => setReplaceReason(e.target.value)} value={replaceReason}>
                <Radio value="Damaged product">Damaged product</Radio>
                <Radio value="Defective product">Defective product</Radio>
                <Radio value="Wrong item received">Wrong item received</Radio>
              </Radio.Group>
            </div>
            <div className="rr-modal-billing">
              <div className="rr-billing-row"><span>Product Cost</span><span className="rr-strikethrough">₹{replaceModal.order?.totalAmount || 0}</span><span className="rr-free">FREE</span></div>
              <div className="rr-billing-row"><span>Processing Fee</span><span className="rr-fee">₹{PROCESSING_FEE}</span></div>
              <div className="rr-billing-total"><span>Total Payable</span><span>₹{PROCESSING_FEE}</span></div>
            </div>
          </div>
        )}
      </Modal>

      {/* Return Modal */}
      <Modal
        title="Return Product"
        open={returnModal.open}
        onCancel={() => setReturnModal({ open: false, order: null, product: null })}
        onOk={handleReturn}
        okText={`Pay ₹${PROCESSING_FEE} & Return`}
        confirmLoading={loading}
        cancelText="Cancel"
      >
        {returnModal.product && (
          <div className="rr-modal-content">
            <div className="rr-modal-product">
              <img src={returnModal.product.primaryImageUrl} alt={returnModal.product.productName} className="rr-modal-img" />
              <div>
                <h4>{returnModal.product.productName}</h4>
                <p>Size: {returnModal.product.size || "N/A"} | Color: {returnModal.product.color || "N/A"}</p>
              </div>
            </div>
            <div className="rr-modal-reason">
              <p className="rr-modal-label">Reason for Return:</p>
              <Radio.Group onChange={(e) => setReturnReason(e.target.value)} value={returnReason}>
                <Radio value="Size doesn't fit">Size doesn't fit</Radio>
                <Radio value="Damaged product">Damaged product</Radio>
                <Radio value="Wrong item received">Wrong item received</Radio>
                <Radio value="Quality not as expected">Quality not as expected</Radio>
                <Radio value="Other">Other</Radio>
              </Radio.Group>
            </div>
            <div className="rr-modal-billing">
              <div className="rr-billing-row"><span>Order Amount</span><span>₹{returnModal.order?.totalAmount || 0}</span></div>
              <div className="rr-billing-row"><span>Processing Fee (non-refundable)</span><span className="rr-fee">₹{PROCESSING_FEE}</span></div>
              <div className="rr-billing-total"><span>Refund Amount</span><span className="rr-refund">₹{returnModal.order?.totalAmount || 0}</span></div>
              <p className="rr-refund-note">₹{PROCESSING_FEE} processing fee is charged separately. Full order amount of ₹{returnModal.order?.totalAmount || 0} will be refunded within 5-7 business days.</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyOrders;